import { DATE_FORMATS } from '@constants/date.ts';
import { EVENT_SOURCES, EVENT_TYPES } from '@constants/eventTypes.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import {
    ASSESSOR_OPEN_NOTIFICATION_TYPES,
    ASSIGNED,
    NON_OPT_OUT_NOTIFICATION_EVENT_TYPES,
    READ,
    UNREAD,
} from '@routes/sidebar/notifications/___shared___/constants.ts';
import type {
    NotificationDetails,
    UserNotificationRequest,
} from '@routes/sidebar/notifications/api/notifications.api.ts';
import type { CommonSearchRequest } from '@types';
import { formatDateBasedOnPreferences, getMonthsAndDaysSinceString } from '@utils/date.ts';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { map, uniqBy } from 'lodash';
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export const useGetDatesTextsBasedOnEventType = (note: NotificationDetails) => {
    const { t } = useLingui();
    const userInfo = useAppSelector((state) => state.user.userInfo);

    const createdUTC = formatDateBasedOnPreferences(
        note.createdUTC,
        userInfo?.preferences.dateFormat ?? DATE_FORMATS.ISO,
    );
    const dueDateUTC = formatDateBasedOnPreferences(
        note.dueDateUTC,
        userInfo?.preferences.dateFormat ?? DATE_FORMATS.ISO,
    );

    const type2text: Record<string, string> = {};
    type2text[EVENT_TYPES.ASSIGNED] = t`Assigned on ${createdUTC}`;
    type2text[EVENT_TYPES.COMPLETED] = t`Completed on ${createdUTC}`;
    type2text[EVENT_TYPES.PASSED] = t`Passed on ${createdUTC}`;
    type2text[EVENT_TYPES.FAILED] = t`Failed on ${createdUTC}`;
    type2text[EVENT_TYPES.OVERDUE] = t`Due date ${dueDateUTC}`;
    type2text[EVENT_TYPES.REMINDER] = t`Due date ${dueDateUTC}`;
    type2text[EVENT_TYPES.FINAL_REMINDER] = t`Due date ${dueDateUTC}`;
    type2text[EVENT_TYPES.ABORTED] = t`Aborted on ${dueDateUTC}`;

    const dateInfo1 = type2text[note.notificationType];
    let dateInfo2 = t`Due date ${dueDateUTC}`;

    if (note.notificationType === EVENT_TYPES.OVERDUE) {
        const overdueMonthsDays = getMonthsAndDaysSinceString(note.dueDateUTC!);
        const { months, days } = overdueMonthsDays;

        if (months === 1 && days === 1) {
            dateInfo2 = t`You missed the deadline ${months} month and ${days} day ago`;
        } else if (months === 1) {
            dateInfo2 = t`You missed the deadline ${months} month and ${days} days ago`;
        } else if (days === 1) {
            dateInfo2 = t`You missed the deadline ${months} months and ${days} day ago`;
        } else {
            dateInfo2 = t`You missed the deadline ${months} months and ${days} days ago`;
        }
    }

    return { dateInfo1, dateInfo2 };
};

// backend no longer sends isNew; a notification is new until it was seen
export const isNotificationNew = (notification: NotificationDetails): boolean => !notification.wasSeen;

export const getGroupedNotificationsByDateRange = (notifications: NotificationDetails[]) => {
    const result: Record<
        'Today' | 'Yesterday' | 'Last Week' | 'Last Month' | 'Last 3 Months' | 'Older',
        NotificationDetails[]
    > = {
        Today: [],
        Yesterday: [],
        'Last Week': [],
        'Last Month': [],
        'Last 3 Months': [],
        Older: [],
    };

    const now = dayjs();

    notifications.forEach((notification) => {
        const createdDate = dayjs(notification.createdUTC);

        if (createdDate.isToday()) {
            result['Today'].push(notification);
        } else if (createdDate.isYesterday()) {
            result['Yesterday'].push(notification);
        } else if (createdDate.isAfter(now.subtract(7, 'day'))) {
            result['Last Week'].push(notification);
        } else if (createdDate.isAfter(now.subtract(1, 'month'))) {
            result['Last Month'].push(notification);
        } else if (createdDate.isAfter(now.subtract(3, 'month'))) {
            result['Last 3 Months'].push(notification);
        } else {
            result['Older'].push(notification);
        }
    });

    return result;
};

type FilterItem = { filter: string | null; value: string }[];

export const getUniqueCapitalizedNotificationSources = (items: FilterItem): string[] => {
    return map(uniqBy(items, 'filter'), (item) =>
        item.filter ? item.filter.charAt(0).toUpperCase() + item.filter.slice(1) : '',
    ).filter(Boolean);
};

export const getUniqueNotificationTypes = (items: FilterItem): string[] => {
    return map(uniqBy(items, 'value'), 'value');
};

// TODO: change when notifications rules provided by back-end
export const showNonOptOutIndicator = (notificationType: string, notificationSource: string) =>
    NON_OPT_OUT_NOTIFICATION_EVENT_TYPES.includes(notificationType) || notificationSource === EVENT_SOURCES.SYSTEM;

export const isAssessorOpenNotification = (notificationType: string) =>
    ASSESSOR_OPEN_NOTIFICATION_TYPES.includes(notificationType);

// ensures that the currently selected tab is always reflected in the API request
export const applyTabConstraint = (activeTab: string, req: UserNotificationRequest) => {
    const base: CommonSearchRequest = {
        pageNumber: req.pageNumber ?? 1,
        pageSize: req.pageSize ?? 10,
        filters: req.filters ?? null,
        textSearch: req.textSearch ?? '',
    };

    if (activeTab === READ) {
        return {
            ...base,
            filters: { ...req.filters, wasRead: ['true'] },
        };
    }

    if (activeTab === UNREAD) {
        return {
            ...base,
            filters: { ...req.filters, wasRead: ['false'] },
        };
    }

    if (activeTab === ASSIGNED) {
        return {
            ...base,
            filters: {
                ...req.filters,
                NotificationType: Array.from(new Set([...(req.filters?.NotificationType ?? []), ASSIGNED])),
            },
        };
    }

    // All tab
    return base;
};
